import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import TaskWatchDialog from 'src/components/MeterTasks/TaskWatchDialog';
import {
  TaskScope,
  WatchedTask,
} from 'src/services/switch-rest-api/meter-task.service';

/**
 * Adds tasks to the task watch dialog, opening it on the first of them. The
 * dialog streams each task's events from `watchMeterTasks` through the scope.
 */
type WatchTasks = (scope: TaskScope, taskIds: string[]) => void;

const TaskWatchContext = createContext<WatchTasks>(() => {
  throw new Error('Component is not wrapped with a TaskWatchProvider.');
});

/** The function that adds tasks to the task watch dialog. */
export const useTaskWatch = () => useContext(TaskWatchContext);

/**
 * Holds the tasks being watched and shows them in a dialog that streams each
 * one's events, for as long as any are open.
 */
export const TaskWatchProvider = ({ children }: { children: ReactNode }) => {
  const [tasks, setTasks] = useState<WatchedTask[]>([]);
  const [selected, setSelected] = useState<string>();

  const watch = useCallback<WatchTasks>((scope, taskIds) => {
    if (taskIds.length === 0) return;
    setTasks((prev) => [
      ...prev,
      ...taskIds
        .filter((taskId) => !prev.some((t) => t.taskId === taskId))
        .map((taskId) => ({ taskId, scope })),
    ]);
    setSelected(taskIds[0]);
  }, []);

  const remove = (taskId: string) => {
    const rest = tasks.filter((t) => t.taskId !== taskId);
    setTasks(rest);
    if (selected === taskId) setSelected(rest[0]?.taskId);
  };

  return (
    <TaskWatchContext.Provider value={watch}>
      {children}
      {tasks.length > 0 && (
        <TaskWatchDialog
          tasks={tasks}
          selected={selected ?? tasks[0].taskId}
          onSelect={setSelected}
          onRemove={remove}
          onClose={() => setTasks([])}
        />
      )}
    </TaskWatchContext.Provider>
  );
};
